import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const IssuanceSuccess = ({ issuanceResult, onNewIssuance, onViewWallet, onViewDashboard }) => {
  const [showDetails, setShowDetails] = useState(false);

  const mockCredentials = [
    {
      id: 'cred_001',
      recipient: 'John Smith',
      email: 'john.smith@email.com',
      status: 'issued',
      transactionHash: '0xa1b2c3d4e5f6789012345678901234567890abcdef1234567890abcdef123456'
    },
    {
      id: 'cred_002',
      recipient: 'Sarah Johnson',
      email: 'sarah.johnson@email.com',
      status: 'issued',
      transactionHash: '0xb2c3d4e5f6789012345678901234567890abcdef1234567890abcdef1234567a'
    },
    {
      id: 'cred_003',
      recipient: 'Michael Chen',
      email: 'michael.chen@email.com',
      status: 'issued',
      transactionHash: '0xc3d4e5f6789012345678901234567890abcdef1234567890abcdef1234567ab2'
    }
  ];

  const handleDownloadReport = () => {
    const reportData = {
      issuanceDate: new Date()?.toISOString(),
      credentialsIssued: issuanceResult?.credentialsIssued,
      transactionHash: issuanceResult?.transactionHash,
      credentials: mockCredentials
    };

    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `credential_issuance_report_${new Date()?.toISOString()?.split('T')?.[0]}.json`;
    document.body?.appendChild(a);
    a?.click();
    document.body?.removeChild(a);
    window.URL?.revokeObjectURL(url);
  };

  const handleShareCredentials = () => {
    const shareData = {
      title: 'Credentials Issued Successfully',
      text: `${issuanceResult?.credentialsIssued} credentials have been successfully issued and verified on the blockchain.`,
      url: window.location?.origin + '/credential-verification'
    };

    if (navigator.share) {
      navigator.share(shareData);
    } else {
      navigator.clipboard?.writeText(`${shareData?.text} ${shareData?.url}`);
      // You could show a toast notification here
    }
  };

  return (
    <div className="space-y-6">
      {/* Success Header */}
      <div className="text-center py-8">
        <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <Icon name="CheckCircle" size={40} className="text-success" />
        </div>
        <h2 className="text-3xl font-bold text-foreground mb-2">Credentials Issued Successfully!</h2>
        <p className="text-muted-foreground text-lg">
          {issuanceResult?.credentialsIssued} credential{issuanceResult?.credentialsIssued > 1 ? 's have' : ' has'} been 
          successfully created and anchored on the blockchain
        </p>
      </div>
      {/* Success Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="credential-card text-center">
          <div className="text-3xl font-bold text-success mb-2">{issuanceResult?.credentialsIssued}</div>
          <div className="text-muted-foreground">Credentials Issued</div>
        </div>
        
        <div className="credential-card text-center">
          <div className="text-3xl font-bold text-primary mb-2">100%</div>
          <div className="text-muted-foreground">Success Rate</div>
        </div>
        
        <div className="credential-card text-center">
          <div className="text-3xl font-bold text-accent mb-2">&lt;30s</div>
          <div className="text-muted-foreground">Processing Time</div>
        </div>
      </div>
      {/* Transaction Details */}
      <div className="credential-card">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="Link" size={20} className="mr-2" />
          Blockchain Transaction
        </h3>
        <div className="space-y-3">
          <div className="flex flex-col sm:flex-row sm:justify-between gap-2">
            <span className="text-muted-foreground">Transaction Hash:</span>
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm text-foreground break-all">
                {issuanceResult?.transactionHash}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigator.clipboard?.writeText(issuanceResult?.transactionHash)}
                iconName="Copy"
              />
            </div>
          </div>
          
          <div className="flex justify-between">
            <span className="text-muted-foreground">Block Confirmation:</span>
            <div className="flex items-center gap-2">
              <Icon name="CheckCircle" size={16} className="text-success" />
              <span className="text-success text-sm">Confirmed</span>
            </div>
          </div>
          
          <div className="flex justify-between">
            <span className="text-muted-foreground">Network:</span>
            <span className="text-foreground">Ethereum Mainnet</span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-muted-foreground">Gas Used:</span>
            <span className="text-foreground">21,000 Gwei</span>
          </div>
        </div>
      </div>
      {/* Credential Details */}
      <div className="credential-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground flex items-center">
            <Icon name="FileText" size={20} className="mr-2" />
            Issued Credentials
          </h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowDetails(!showDetails)}
            iconName={showDetails ? "ChevronUp" : "ChevronDown"}
          >
            {showDetails ? 'Hide' : 'Show'} Details
          </Button>
        </div>

        {showDetails && (
          <div className="space-y-3">
            {mockCredentials?.slice(0, issuanceResult?.credentialsIssued)?.map((credential, index) => (
              <div key={credential?.id} className="border border-border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h4 className="font-medium text-foreground">{credential?.recipient}</h4>
                    <p className="text-sm text-muted-foreground">{credential?.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="CheckCircle" size={16} className="text-success" />
                    <span className="text-success text-sm capitalize">{credential?.status}</span>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground font-mono">
                  ID: {credential?.id}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Next Steps */}
      <div className="credential-card">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="ArrowRight" size={20} className="mr-2" />
          What's Next?
        </h3>
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <Icon name="Mail" size={16} className="text-primary mt-1" />
            <div>
              <h4 className="font-medium text-foreground">Recipients Notified</h4>
              <p className="text-sm text-muted-foreground">
                All recipients have been automatically notified via email with instructions to claim their credentials
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <Icon name="Wallet" size={16} className="text-primary mt-1" />
            <div>
              <h4 className="font-medium text-foreground">Added to Digital Wallet</h4>
              <p className="text-sm text-muted-foreground">
                Credentials are now available in the recipients' digital wallets for sharing and verification
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <Icon name="Shield" size={16} className="text-primary mt-1" />
            <div>
              <h4 className="font-medium text-foreground">Verification Ready</h4>
              <p className="text-sm text-muted-foreground">
                Credentials can now be verified by third parties using QR codes or direct verification links
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-border">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <Button
            variant="outline"
            onClick={handleDownloadReport}
            iconName="Download"
            iconPosition="left"
          >
            Download Report
          </Button>
          
          <Button
            variant="outline"
            onClick={handleShareCredentials}
            iconName="Share"
            iconPosition="left"
          >
            Share Results
          </Button>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            variant="outline"
            onClick={onViewWallet}
            iconName="Wallet"
            iconPosition="left"
          >
            View Wallet
          </Button>
          
          <Button
            variant="secondary"
            onClick={onViewDashboard}
            iconName="LayoutDashboard"
            iconPosition="left"
          >
            Dashboard
          </Button>
          
          <Button
            onClick={onNewIssuance}
            iconName="Plus"
            iconPosition="left"
          >
            Issue More
          </Button>
        </div>
      </div>
      {/* Additional Resources */}
      <div className="credential-card bg-muted/30">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="BookOpen" size={20} className="mr-2" />
          Resources & Support
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <h4 className="font-medium text-foreground">Documentation</h4>
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li className="flex items-center gap-2">
                <Icon name="ExternalLink" size={12} />
                <span>Credential Verification Guide</span>
              </li>
              <li className="flex items-center gap-2">
                <Icon name="ExternalLink" size={12} />
                <span>API Integration Documentation</span>
              </li>
              <li className="flex items-center gap-2">
                <Icon name="ExternalLink" size={12} />
                <span>Best Practices for Issuers</span>
              </li>
            </ul>
          </div>
          
          <div className="space-y-2">
            <h4 className="font-medium text-foreground">Support</h4>
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li className="flex items-center gap-2">
                <Icon name="MessageCircle" size={12} />
                <span>Live Chat Support</span>
              </li>
              <li className="flex items-center gap-2">
                <Icon name="Mail" size={12} />
                <span>Email: support@verifychain.com</span>
              </li>
              <li className="flex items-center gap-2">
                <Icon name="Phone" size={12} />
                <span>Phone: +1 (555) 123-4567</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IssuanceSuccess;